import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { type TableColumn } from "react-data-table-component";
import { Typography } from "../atoms/Typography";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { ContractDocument } from "../../types/contractDocument";
import type { ContractsSortField } from "../../services/contractsService";
import { Eye, FileText } from "lucide-react";
import { createDataTablePagination } from "./DataTablePagination";

const contractDocumentsTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 20, 30],
});

const actionIconBtn =
  "flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 transition-all duration-200 ease-in-out hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40";

type ContractDocumentsTableProps = {
  readonly documents: ContractDocument[];
  /** Total rows for the current server query (used for empty-state copy). */
  readonly listTotal: number;
  readonly hasActiveSearch: boolean;
  readonly hasActiveTypeFilter: boolean;
  readonly serverSortField: ContractsSortField;
  readonly serverSortDir: "asc" | "desc";
  readonly onServerSort: (field: ContractsSortField, direction: "asc" | "desc") => void;
};

const StatusBadge = ({ status }: { readonly status: string }) => {
  const normalized = status.toLowerCase();
  const styles =
    normalized.includes("approved") ||
      normalized.includes("completed") ||
      normalized.includes("ready")
      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
      : normalized.includes("fail") || normalized.includes("error")
        ? "bg-red-50 border-red-200 text-red-700"
        : normalized.includes("review")
          ? "bg-amber-50 border-amber-200 text-amber-700"
          : "bg-sky-50 border-sky-200 text-sky-700";

  return (
    <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${styles}`}>
      {status}
    </span>
  );
};

/** View/open actions follow pipeline stage, not business `status`. */
const canViewByStage = (stage: string): boolean => {
  const normalized = stage.toLowerCase();
  return (
    normalized.includes("review") ||
    normalized.includes("ready") ||
    normalized.includes("completed")
  );
};

const ActionButtons = ({
  doc,
  onView,
}: {
  readonly doc: ContractDocument;
  readonly onView: (id: string) => void;
}) => {
  const canView = canViewByStage(doc.stage);

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={() => {
          if (canView) onView(doc.id);
        }}
        disabled={!canView}
        title={
          canView ? `View ${doc.documentName}` : "Available once processing stage reaches Review"
        }
        className={actionIconBtn}
        aria-label={`View ${doc.documentName}`}
        aria-disabled={!canView}
      >
        <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
      </button>
    </div>
  );
};
function emptyStateMessage(
  listTotal: number,
  hasActiveSearch: boolean,
  hasActiveTypeFilter: boolean,
): string {
  if (listTotal === 0 && !hasActiveSearch && !hasActiveTypeFilter) {
    return "No approved contracts yet. Upload a contract to get started.";
  }
  if (hasActiveSearch || hasActiveTypeFilter) {
    return "No contracts match your search or filter. Try adjusting your criteria.";
  }
  return "No contracts to display.";
}

const SERVER_SORTABLE: readonly ContractsSortField[] = [
  "name",
  "contract_type",
  "created_by",
  "status",
  "created_at",
];

export const ContractDocumentsTable = ({
  documents,
  listTotal,
  hasActiveSearch,
  hasActiveTypeFilter,
  serverSortField,
  serverSortDir,
  onServerSort,
}: ContractDocumentsTableProps) => {
  const navigate = useNavigate();

  const columns: TableColumn<ContractDocument>[] = useMemo(
    () => [
      {
        id: "name",
        name: "Document Name",
        selector: (row) => row.documentName,
        sortable: true,
        grow: 2,
        cell: (row) => {
          const lower = row.documentName.toLowerCase();
          const isPdf = lower.endsWith(".pdf");
          return (
            <div className="flex items-center gap-2.5">
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${isPdf ? "bg-red-50 text-red-600" : "bg-sky-50 text-sky-600"
                  }`}
                aria-hidden
              >
                <FileText className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="">{row.documentName}</span>
            </div>
          );
        },
      },
      {
        id: "contract_type",
        name: "Contract Type",
        selector: (row) => row.contractType,
        sortable: true,
      },
      {
        id: "created_by",
        name: "Created By",
        selector: (row) => row.createdBy,
        sortable: true,
        cell: (row) => (
          <div>
            <p className="">{row.createdBy}</p>
            <p className="text-xs font-light text-[#6b7280]">{row.createdByEmail}</p>
          </div>
        ),
      },
      {
        id: "status",
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: "created_at",
        name: "Created Date",
        selector: (row) => row.createdDate,
        sortable: true,
        cell: (row) => <span className="font-light text-[#6b7280]">{row.createdDate}</span>,
      },
      {
        name: "Actions",
        cell: (row) => <ActionButtons doc={row} onView={(id) => navigate(`/contracts/${id}`)} />,
        width: "120px",
        right: true,
      },
    ],
    [navigate],
  );

  return (
    <DataTable
      key={`${serverSortField}-${serverSortDir}`}
      columns={columns}
      data={documents}
      sortServer
      onSort={(column, direction) => {
        const raw = column.id;
        if (raw === undefined || raw === null) {
          return;
        }
        const id = String(raw) as ContractsSortField;
        if (!SERVER_SORTABLE.includes(id)) {
          return;
        }
        onServerSort(id, direction);
      }}
      defaultSortFieldId={serverSortField}
      defaultSortAsc={serverSortDir === "asc"}
      customStyles={dataTableCustomStyles}
      striped={false}
      pagination
      paginationPerPage={10}
      paginationRowsPerPageOptions={[10, 20, 30]}
      paginationComponent={contractDocumentsTablePagination}
      noDataComponent={
        <div className="p-12 text-center">
          <Typography variant="muted">
            {emptyStateMessage(listTotal, hasActiveSearch, hasActiveTypeFilter)}
          </Typography>
        </div>
      }
      highlightOnHover
      responsive
    />
  );
};
